/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Dialog, makeStyles, Typography } from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useTranslation } from 'react-i18next';
import useDuplicateImportContactDialog from './useDuplicateImportContactDialog';
import DuplicateTable from './DuplicateTable';
import ImportStrategyForm from './ImportStrategyForm';
import Button from '../../common/Button';
import { ImportStatus } from '../importStatus';
import AlertDialog from '../../common/alertDialog/AlertDialog';

const useStyles = makeStyles(theme => ({
  duplicatesFound: {
    fontWeight: theme.typography.fontWeightBold,
    margin: '1rem 2rem',
  },
  tableWrapper: {
    margin: '0rem 2rem 1rem',
  },
  importFormWrapper: {
    margin: '1rem 2rem',
  },
}));

export default function DuplicateImportContactDialog(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { importResult, onAllDuplicatesResolved, onCancelImport } = props;
  const {
    strategy,
    applyToAll,
    duplicatesPage,
    currentDuplicate,
    resolvedList,
    onStrategyChange,
    onApplyToAllChange,
    onImport,
    onCancel,
    showCancelConfirm,
    setShowCancelConfirm,
  } = useDuplicateImportContactDialog(importResult, onAllDuplicatesResolved, onCancelImport);

  const totalNumberOfDuplicates = duplicatesPage?.totalElements || 0; // Try with > 1 search for useMemo()
  const remainingDuplicates = totalNumberOfDuplicates - resolvedList?.length || 0;

  return (
    <Dialog open={importResult.status === ImportStatus.MERGING} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h2">
          {t('import.contact')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {totalNumberOfDuplicates > 1 ? t('messages.contact.several.existing') : t('messages.contact.one.existing')}
        {' '}
        {t('messages.contact.compare')}
        <div className={classes.duplicatesFound}>
          {totalNumberOfDuplicates > 1
            ? t('messages.contact.duplicate.found', {
              currentDuplicate: resolvedList.length + 1,
              allDuplicates: totalNumberOfDuplicates,
            })
            : t('messages.contact.one.duplicate')}
        </div>
        {currentDuplicate && (
          <div className={classes.tableWrapper}>
            <DuplicateTable duplicate={currentDuplicate} />
          </div>
        )}
        <div className={classes.importFormWrapper}>
          <ImportStrategyForm
            remainingDuplicates={remainingDuplicates}
            strategy={strategy}
            applyToAll={applyToAll}
            onStrategyChange={onStrategyChange}
            onApplyToAllChange={onApplyToAllChange}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => setShowCancelConfirm(true)}>
          {t('actions.cancel')}
        </Button>
        <Button color="primary" disabled={!strategy} onClick={onImport}>
          {t('Import')}
        </Button>
      </DialogActions>
      <AlertDialog
        isOpen={!!showCancelConfirm}
        title="messages.are-you-sure"
        message="messages.confirmation.cancel.import"
        confirmButtonMessage="actions.import.cancel"
        handleConfirmButton={onCancel}
        showCancelButton={true}
        cancelButtonMessage="actions.import.continue"
        handleCancelButton={() => setShowCancelConfirm(false)}
      />
    </Dialog>
  );
}
